import React, { useState, useCallback, useMemo, FormEvent } from 'react';
import { TextField } from '@td-client-frontend/common';
import Modal from '../../../components/Modal/NewModal';
import { useTemplateList } from '../../../hooks/useTemplateList';
import { Button } from '../../../components/Button';
import { useDebounce } from 'use-debounce';

export enum TemplateModalType {
  UPDATE = 'update',
  CREATE = 'create'
}
interface ModalProps {
  oldTemplateName?: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
  type: TemplateModalType;
}

const modalParams = (type: TemplateModalType, oldName: string | undefined) => {
  const data = {
    update: { title: `Rename Template: ${oldName}`, submitBtn: 'Rename' },
    create: { title: 'Create New Template', submitBtn: 'Create' }
  };
  return data[type];
};

const MIN_TEMPLATE_LENGTH = 3;

export const TemplateNameModal = ({ oldTemplateName, isOpen, onClose, onSubmit, type }: ModalProps) => {
  const { title, submitBtn } = useMemo(() => modalParams(type, oldTemplateName), [type, oldTemplateName]);
  const [templateName, setTemplateName] = useState<string>('');

  const [debounceTemplateName] = useDebounce(templateName, 750);

  const { data: templateList = [], isLoading } = useTemplateList({ search: debounceTemplateName });

  const errors = useMemo(() => {
    const errors = new Set();
    let stringifiedErrors = '';
    if (templateList?.some((name) => name.toUpperCase() === templateName.toUpperCase())) {
      errors.add('Template Name is already in use');
    }

    if (templateName && templateName.length < MIN_TEMPLATE_LENGTH) {
      errors.add(`Template Name is less than ${MIN_TEMPLATE_LENGTH} characters long`);
    }

    // Incase we add more errors, the stucture is provided
    if (errors.size > 1) {
      stringifiedErrors = Array.from(errors).join(' and ');
    } else {
      stringifiedErrors = Array.from(errors).join();
    }

    return stringifiedErrors;
  }, [templateList, templateName]);

  const isEmptyTemplateName = useMemo(() => debounceTemplateName === '', [debounceTemplateName]);

  const validationError = useMemo(() => {
    return errors.length > 1 || (debounceTemplateName.length < MIN_TEMPLATE_LENGTH && !isEmptyTemplateName);
  }, [debounceTemplateName.length, errors.length, isEmptyTemplateName]);

  const handleChange = useCallback((e: FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setTemplateName(value.replace(/\s{2,}/g, ' '));
  }, []);
  const handleCloseModal = useCallback(() => {
    setTemplateName('');
    onClose();
  }, [onClose]);

  const handleCreate = useCallback(() => {
    onSubmit(templateName);
    handleCloseModal();
  }, [onSubmit, templateName, handleCloseModal]);

  const ButtonGroup = useCallback(
    () => (
      <div className="flex gap-4">
        <Button kind="secondary" onClick={handleCloseModal} title="Cancel" />
        <Button
          onClick={handleCreate}
          tooltip="Please enter all required fields"
          id="create-template-button"
          disabled={validationError || isLoading || isEmptyTemplateName}
          title={submitBtn}
        />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleCloseModal, isEmptyTemplateName, isLoading, handleCreate, validationError]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={title}
      size="small"
      customFooterActions={{
        right: <ButtonGroup />
      }}
    >
      <section className="p-6 w-full">
        <TextField
          placeholder="Enter New Template Name"
          label="New Template Name"
          error={errors}
          isError={validationError}
          onChange={handleChange}
        />
      </section>
    </Modal>
  );
};
